/**
 * The header line that says what a refresh run *is*.
 *
 * Same problem the enrichment header solves, different constants. On a refresh run
 * `trigger_type` is `cron` for all of them (the sweep rides the cron tick) and `trigger_spec.sites`
 * is either absent or a carry-site list, so the scrape header's provenance fields say nothing that
 * varies. What identifies a refresh run is its **hour**: the sweep does a find-or-create on
 * `refresh_run_hour_key`, so the hour is the run's identity, not a timestamp — and it is why a
 * duplicate-hook job seeded at 14:20 lands in the 14:00 run rather than in a run of its own.
 */
import { Clock, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

export function RefreshRunBadge({ hour }: { hour: string | null }) {
  return (
    <Badge className="bg-rose-500/15 text-rose-700 hover:bg-rose-500/15 dark:text-rose-300">
      <RefreshCw className="mr-1 h-3 w-3" />
      refresh{hour ? ` · ${hour}` : ""}
    </Badge>
  );
}

export function RefreshRunMeta({ hour }: { hour: string | null }) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
      <span className="inline-flex items-center gap-1.5">
        <Clock className="h-3 w-3" />
        {hour ? (
          <span className="font-mono tabular-nums">{hour}</span>
        ) : (
          <span>this hour&apos;s batch</span>
        )}
      </span>
      <Separator orientation="vertical" className="h-3" />
      <span>
        one batch of the nightly sweep — published events whose refresh cadence
        came due, plus any duplicate-hook job seeded in the same hour
      </span>
    </div>
  );
}
